import React from "react";
import { Button } from "../..";
import { Modal } from "react-bootstrap";
import { FileDetailsForm } from "./components/FileDetailsForm";
import { FileUploadForm } from "./components/FileUploadForm";
import { ProcessFileMessage } from "./components/ProcessFileMessage";
import { DocumentUploadModalForm, DocumentUploadModalResponse, DocumentUploadModalProps, OpenDocumentUploadModalOptions } from "./types";
import { useDropzone } from "react-dropzone";
import { useAutoAnimate } from "@formkit/auto-animate/react";
import { FormProvider, useForm } from "react-hook-form";
import { confirmable, createConfirmation } from "../../../hooks/DialogMountPoint";
import { ClientProjectProvider } from "../../../hooks/ClientProjectContext";
import { useFileNameChecker } from "./hooks/useFileNameChecker";
import { useUploadDocument } from "./hooks/useUploadDocument";
import { documentUploadProps, DocumentTypes } from "./config";
import ThemedModal from "../../utilities/ThemedModal";

export const DocumentUploadModal = ({
    backdrop = 'static',
    cancel,
    centered = true,
    defaultValues,
    dialogId,
    dismiss,
    masterAccountId,
    proceed,
    show,
    size = "lg",
    userId,
    ...rest
} : Readonly<DocumentUploadModalProps>) => {
    const [bodyAnimation] = useAutoAnimate();
    const { checkFileName, isLoading: isCheckingFile } = useFileNameChecker();
    const { uploadDocument, isLoading: isUploading } = useUploadDocument();
    const [fileDataIcon, setFileDataIcon] = React.useState<string>('fa-file');
    const [isFileNameValid, setIsFileNameValid] = React.useState<boolean>(true);
    const formMethods = useForm<DocumentUploadModalForm>();
    const { handleSubmit, reset, setValue, watch } = formMethods;
    const fileData = watch('fileData');

    const onClose = () => cancel!();

    const onDrop = React.useCallback(async (acceptedFiles: File[] ) => {
        if (acceptedFiles.length === 0) {
            return;
        }

        const { name } = acceptedFiles[0];
        const isFileNameValid = await checkFileName(name, masterAccountId);

        setIsFileNameValid(() => isFileNameValid);
        setValue('fileData', acceptedFiles[0], { shouldDirty: true });
    }, []);
    
    const onSubmit = (form: DocumentUploadModalForm) => {
        uploadDocument(form).then((r) => {
            if (r.status < 300) {
                proceed!(r);
            }
        })
    };

    // whenever the default values change/we basically open the modal here.
    React.useEffect(() => {
        if (!defaultValues) {
            return;
        }
        reset({ ...defaultValues });
    }, [ defaultValues ]);

    // whenever the file data is changed
    React.useEffect(() => {
        if (!fileData) {
            reset({ ...defaultValues });
            return;
        }

        const fileTypeData = DocumentTypes.find(el => el.mimeTypes.includes(fileData.type));
        setFileDataIcon(fileTypeData?.icon ?? 'fa-file');

        reset({
            ...defaultValues,
            fileData: fileData,
            fileName: fileData.name,
            fileSize: fileData.size,
            documentType: fileTypeData.value
        });
    }, [ fileData ]);
    
    const dropzone = useDropzone({ ...documentUploadProps, onDrop });
    const isButtonsDisabled = dropzone.isFileDialogActive || isUploading;
    return (
        <Modal backdrop={backdrop} centered={centered} size={size} show={show} onHide={onClose} {...rest}>
            <ThemedModal.Header>
                <Modal.Title>{masterAccountId ? "Upload Document to Master Account" : "Upload Personal Document"}</Modal.Title>
            </ThemedModal.Header>
            <FormProvider {...formMethods}>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <Modal.Body ref={bodyAnimation}>
                        <FileUploadForm dropzone={dropzone} isLoading={isCheckingFile} />
                        {((fileData && isCheckingFile) || isUploading) && (
                            <ProcessFileMessage message={isUploading ? 'Uploading file...' : 'Processing file...'} />
                        )}
                        {!dropzone.isFileDialogActive && !isUploading && (
                            <>
                                {fileData && (
                                    <FileDetailsForm 
                                        fileIcon={fileDataIcon} 
                                        isFileNameValid={isFileNameValid} 
                                        setIsFileNameValid={(v: boolean) => setIsFileNameValid(() => v)} 
                                        userId={userId}
                                    />
                                )}
                            </>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        {fileData && (
                            <Button variant="success" type="submit" disabled={isButtonsDisabled}>Upload</Button>
                        )}
                        <Button variant="light" type="button" onClick={onClose} disabled={isButtonsDisabled}>Cancel</Button>
                    </Modal.Footer>
                </form>
            </FormProvider>
        </Modal>
    );
};

export function openDocumentUploadModal(options: OpenDocumentUploadModalOptions) : Promise<DocumentUploadModalResponse> {
    return createConfirmation<DocumentUploadModalProps, DocumentUploadModalResponse>(confirmable((props) => {
        return options.masterAccountId
            ? (
                <ClientProjectProvider masterAccountId={options.masterAccountId!}>
                    <DocumentUploadModal {...props} />
                </ClientProjectProvider>
            )
            : <DocumentUploadModal {...props} />
    }))({
        masterAccountId: options.masterAccountId ?? null,
        dialogId: 'document_upload_modal',
        defaultValues: { 
            adviserId: options.adviserId ?? null,
            divisionId: options.divisionId,
            masterAccountId: options.masterAccountId ?? null,
            projectId: options.projectId ?? null,
            fileName: '',
            fileSize: 0,
            fileData: null,
            documentType: null,
            documentFunctionId: null,
            documentDescription: null,
            conflictBehaviour: 'rename',
            isClientVisible: false,
            isClientSignaturesRequired: false,
            requiredSignatures: []
        },
        userId: options.userId
    });
}